import { Priority, PriorityConfig, VisibleToUser } from "../types";

export const getDaysUntilDue = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "No deadline";

  const dueDate = new Date(dateString);
  const today = new Date();

  // Reset hours for accurate day calculation
  const dueDateOnly = new Date(
    dueDate.getFullYear(),
    dueDate.getMonth(),
    dueDate.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const diffTime = dueDateOnly.getTime() - todayOnly.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `Overdue by ${Math.abs(diffDays)} day(s)`;
  } else if (diffDays === 0) {
    return "Due Today";
  } else if (diffDays === 1) {
    return "Due Tomorrow";
  } else {
    return `${diffDays} days left`;
  }
};

export const getPriorityConfig = (priority: Priority): PriorityConfig => {
  const configs: Record<Priority, PriorityConfig> = {
    high: { color: "danger", icon: "ti-alert-circle" },
    medium: { color: "info", icon: "ti-clock" },
    low: { color: "success", icon: "ti-check-circle" },
  };
  return configs[priority] || configs.medium;
};

// Helper to determine priority based on days until due
export const calculatePriority = (
  dateString: string | null | undefined
): Priority => {
  if (!dateString) return "low";

  const dueDate = new Date(dateString);
  const today = new Date();
  const diffTime = today.getTime() - dueDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 1) return "low";
  else return "high";
};

// Helper to get assigned by name from visible_to array
export const getAssignedByName = (
  visibleToArray: VisibleToUser[] | undefined
): string => {
  if (!visibleToArray || visibleToArray.length === 0) {
    return "Not assigned";
  }

  // Return the first person's name, or you can customize this logic
  const firstAssigned = visibleToArray[0];
  return (
    `${firstAssigned.fname} ${firstAssigned.lname}`.trim() ||
    firstAssigned.username ||
    "Unknown"
  );
};

// Additional helper to format multiple assignees
export const getAllAssigneeNames = (
  visibleToArray: VisibleToUser[] | undefined
): string => {
  if (!visibleToArray || visibleToArray.length === 0) {
    return "Not assigned";
  }

  if (visibleToArray.length === 1) {
    return getAssignedByName(visibleToArray);
  }

  const names = visibleToArray.map(
    (user) => `${user.fname} ${user.lname}`.trim() || user.username
  );

  if (names.length <= 2) {
    return names.join(" and ");
  }

  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
};

// Helper to check if report is urgent
export const isUrgentReport = (
  dateString: string | null | undefined
): boolean => {
  const priority = calculatePriority(dateString);
  return priority === "high";
};

// Helper to get status badge color based on priority
export const getStatusColor = (
  dateString: string | null | undefined
): string => {
  const priority = calculatePriority(dateString);
  const config = getPriorityConfig(priority);
  return config.color;
};

export const getStatusConfig = (status: string | any) => {
  let config;
  switch (status) {
    case "submitted":
      config = {
        bgClass: "bg-success",
        textClass: "text-white",
        icon: "ti-circle-check",
        iconColor: "text-success",
        progressColor: "success",
      };
      break;
    case "draft":
      config = {
        bgClass: "bg-info",
        textClass: "text-white",
        icon: "ti-edit",
        iconColor: "text-info",
        progressColor: "info",
      };
      break;
    default:
      config = {
        bgClass: "bg-secondary",
        textClass: "text-white",
        icon: "ti-clock",
        iconColor: "text-secondary",
        progressColor: "secondary",
      };
      break;
  }

  return config;
};

export const getReportTypeBadgeColor = (type: string): string => {
  const typeColors: Record<string, string> = {
    "Sales Report": "primary",
    "Performance Report": "info",
    "Customer Report": "success",
    "Financial Report": "warning",
  };

  return typeColors[type] || "secondary";
};
